import { AuthError } from '@classgo/shared/auth';
import { PRODUCT_NAME } from '@classgo/shared/brand';
import { Button, Input } from '@classgo/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter, useSearch } from '@tanstack/react-router';
import QRCode from 'qrcode';
import { type FormEvent, useEffect, useState } from 'react';

import { useApi } from '../../api/provider';
import { sessionQuery } from '../../api/session-query';
import { useAuthState, useCognito } from '../../auth/auth-provider';
import { useKitT } from '../../i18n';
import { AuthLayout, FormError } from '../auth-layout';

const isInternalPath = (value: string | undefined): value is string =>
  typeof value === 'string' && value.startsWith('/') && !value.startsWith('//');

const otpauthUri = (email: string, secret: string): string => {
  const issuer = encodeURIComponent(PRODUCT_NAME);
  return `otpauth://totp/${issuer}:${encodeURIComponent(email)}?secret=${secret}&issuer=${issuer}&digits=6&period=30`;
};

/**
 * Forced TOTP enrollment for roles that require it (ClassGO staff and school owners, ticket #54).
 * The person scans the QR (or types the key) in their authenticator app, enters the first code,
 * and the app enables TOTP in Cognito and confirms it with the API, which then stops refusing
 * their requests. Reached from the router guard right after login; leaves to where they were going.
 */
export const MfaEnrollScreen = () => {
  const t = useKitT();
  const router = useRouter();
  const api = useApi();
  const cognito = useCognito();
  const queryClient = useQueryClient();
  const { email } = useAuthState();
  const search = useSearch({ strict: false }) as { redirect?: string | undefined };
  const [secret, setSecret] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const fail = (thrown: unknown) =>
    setError(
      thrown instanceof AuthError ? t(`auth.error.${thrown.code}`) : t('auth.error.UNKNOWN'),
    );

  useEffect(() => {
    if (!email) return;
    let cancelled = false;
    cognito
      .beginTotpEnrollment(email)
      .then(async (started) => {
        const image = await QRCode.toDataURL(otpauthUri(email, started.secret), {
          margin: 1,
          width: 192,
        });
        if (cancelled) return;
        setSecret(started.secret);
        setQr(image);
      })
      .catch((thrown: unknown) => {
        if (!cancelled) fail(thrown);
      });
    return () => {
      cancelled = true;
    };
  }, [cognito, email]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!email) return;
    setBusy(true);
    setError(null);
    try {
      await cognito.completeTotpEnrollment(email, code.trim());
      await api.request('/me/mfa/confirm', { method: 'POST' });
      // ensureQueryData in the layout loader would hand back the stale session; fetch it now.
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      await queryClient.fetchQuery(sessionQuery(api));
      router.history.push(isInternalPath(search.redirect) ? search.redirect : '/');
    } catch (thrown) {
      fail(thrown);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={t('mfa.title')} subtitle={t('mfa.help')}>
      <form onSubmit={submit} className="space-y-4" noValidate>
        <FormError message={error} />
        <div className="flex flex-col items-center gap-3 rounded-control bg-gray-50 px-3 py-4">
          {qr ? (
            <img src={qr} alt={t('mfa.qrAlt')} width={192} height={192} />
          ) : (
            <p className="text-sm text-gray-500">{t('common.loading')}</p>
          )}
          <div className="w-full text-sm">
            <span className="block text-xs font-medium text-gray-500">{t('mfa.secret')}</span>
            <code className="break-all text-gray-900">{secret ?? t('common.loading')}</code>
          </div>
        </div>
        <Input
          label={t('mfa.code')}
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          required
          value={code}
          onChange={(event) => setCode(event.target.value)}
        />
        <Button type="submit" loading={busy} disabled={!secret} className="w-full">
          {t('mfa.submit')}
        </Button>
        <p className="text-xs text-gray-500">{t('mfa.recovery')}</p>
      </form>
    </AuthLayout>
  );
};
